import type { ComponentType, CSSProperties, ReactNode } from "react"
import { memo, useCallback, useMemo } from "react"
import { IconMsgOutline18 as MessageCircle } from "nucleo-ui-outline-18"

import type { Request } from "@productlane/api/src/lib/support-portal/request.types"
import { formatPortalDayMonth } from "@productlane/lib/src/portal-date"
import { getStatusLabel } from "@productlane/lib/src/roadmap-status"
import {
	getRequestDisplayDate,
	getRequestIssueIdentifier,
} from "@productlane/lib/src/support-requests"

import type { ProjectStatusTypes } from "../../icons/project-status-icons"
import { IconMap } from "../../icons/issue-status-icons"
import { ProjectStatusColors } from "../../icons/project-status-icons"
import { Avatar } from "../avatar"
import { Badge } from "../badge"
import { DueDatePill } from "../due-date-pill"
import { Tooltip } from "../tooltip"

type ProjectIconResolver = (
	icon: string | null | undefined,
) => ComponentType<{ className?: string; style?: CSSProperties }>

const defaultT = (_key: string, fallback: string) => fallback

interface Props {
	request: Request
	onClick?: () => void
	isFocused?: boolean
	onMouseEnter?: () => void
	onMouseLeave?: () => void
	isDueDateEnabled?: boolean
	getProjectIcon: ProjectIconResolver
	t?: (key: string, fallback: string) => string
	/** Injected priority control. Omit for a read-only list. */
	renderPriority?: (request: Request) => ReactNode
	/** Pointer cursor on the row. Off in the dashboard app per house style. */
	showPointer?: boolean
}

export const RequestListItem = memo(
	({
		request,
		onClick,
		isFocused = false,
		onMouseEnter,
		onMouseLeave,
		isDueDateEnabled = false,
		getProjectIcon,
		t = defaultT,
		renderPriority,
		showPointer = true,
	}: Props) => {
		const RequestIcon = useCallback(() => {
			if (request.type === "project") {
				const ProjectIcon = getProjectIcon(request.item.icon)
				const state = (request.item.state || "backlog") as ProjectStatusTypes
				return (
					<div className="flex items-center gap-0.5">
						{!!ProjectIcon && (
							<ProjectIcon
								className="h-auto w-[1rem] min-w-[1rem]"
								style={{
									color: ProjectStatusColors[state],
								}}
							/>
						)}
					</div>
				)
			}
			if (request.type === "issue") {
				const IssueIcon = IconMap[request.item.status]
				const displayStatus = getStatusLabel(
					request.item.statusName || request.item.status,
					t,
				)
				return (
					<>
						{!!IssueIcon && (
							<Tooltip
								label={`${t("requests.status", "Status")}: ${displayStatus}`}
								shouldWrapChildren
							>
								<IssueIcon
									className="h-4 min-h-4 w-4 min-w-4"
									color={request.item.statusColor || "currentColor"}
								/>
							</Tooltip>
						)}
					</>
				)
			}
			if (request.type === "feedback") {
				return (
					<MessageCircle className="h-4 min-h-4 w-4 min-w-4 text-gray-400 dark:text-gray-600" />
				)
			}
		}, [request, t, getProjectIcon])

		const requestTitle = useMemo(() => {
			if (request.type === "issue") {
				return request.item.publicTitle || request.item.title
			}
			if (request.type === "project") {
				return request.item.publicTitle || request.item.name
			}
			return request.item.title || t("navigation.requests", "Requests")
		}, [request, t])

		const issueIdentifier = useMemo(
			() => getRequestIssueIdentifier(request),
			[request],
		)

		const requestDueDate = useMemo(() => {
			if (request.type === "project") {
				if (request.item.dueDateRaw) {
					return request.item.dueDateRaw
				}
				if (request.item.targetDate) {
					return request.item.targetDate.toISOString()
				}
				return request.item.dueDate
			}
			if (request.type === "issue") {
				return request.item.dueDateRaw || request.item.dueDate
			}
			return null
		}, [request])

		const requestDueDateResolution = useMemo(() => {
			if (request.type === "project" || request.type === "issue") {
				return request.item.dueDateResolution || null
			}
			return null
		}, [request])

		// A company-only thread has no person on it, so the company stands in as
		// the submitter.
		const requestSubmitter = useMemo(() => {
			if (request.contact) {
				return {
					name: request.contact.name,
					email: request.contact.email,
					imageUrl: request.contact.imageUrl,
				}
			}
			if (request.company) {
				return {
					name: request.company.name,
					email: null,
					imageUrl: request.company.logoUrl,
				}
			}
			return null
		}, [request])

		const formatDisplayDate = useMemo(() => {
			const displayDate = getRequestDisplayDate(request)
			if (!displayDate) {
				return ""
			}
			return formatPortalDayMonth(displayDate)
		}, [request])

		return (
			<div
				role="button"
				tabIndex={0}
				className={`group block w-full select-none px-3 py-2 text-left no-underline hover:bg-gray-25 sm:px-3 sm:py-3 hover:dark:bg-white/[0.02] ${showPointer ? "cursor-pointer" : ""} ${isFocused ? "bg-gray-50 dark:bg-white/[0.02]" : ""}`}
				onClick={() => onClick?.()}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault()
						onClick?.()
					}
				}}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
			>
				<div className="flex items-center justify-between gap-4">
					{/* Left side */}
					<div className="flex min-w-0 flex-1 items-center gap-3">
						{/* Icon */}
						<RequestIcon />

						{/* Title */}
						<div className="flex min-w-0 flex-1 items-center gap-3">
							<div className="flex min-w-0 items-center gap-2">
								{!!issueIdentifier && (
									<span className="flex-none text-[12px] tabular-nums text-tertiary md:text-[13px]">
										{issueIdentifier}
									</span>
								)}
								<div className="min-w-0 truncate text-[12px] font-medium tracking-[-0.2px] text-primary md:text-[13px]">
									{requestTitle}
								</div>
							</div>
							{request.needsResponse && (
								<Badge variant="important" className="hidden sm:inline-flex">
									<span className="h-1.5 w-1.5 flex-none rounded-full bg-green-500" />
									{t("requests.needsResponse", "Needs your response")}
								</Badge>
							)}
						</div>
					</div>

					{/* Right side: Single row with all badges and info */}
					<div className="flex flex-shrink-0 items-center gap-2">
						{isDueDateEnabled && (
							<DueDatePill
								dueDate={requestDueDate}
								dueDateResolution={requestDueDateResolution}
								variant="important"
								iconClassName="[color:var(--portal-accent-color)]"
							/>
						)}

						{renderPriority && (
							<div className="hidden sm:flex">{renderPriority(request)}</div>
						)}

						<div className="mr-1.5 w-12 text-right sm:w-14">
							<span className="text-[11px] text-tertiary md:text-[13px]">
								{formatDisplayDate}
							</span>
						</div>

						{requestSubmitter && (
							<Tooltip
								label={
									<div className="flex flex-col">
										<div className="font-semibold text-primary">
											{requestSubmitter.name || requestSubmitter.email}
										</div>
										{requestSubmitter.name && requestSubmitter.email && (
											<div>{requestSubmitter.email}</div>
										)}
									</div>
								}
								shouldWrapChildren
							>
								<Avatar
									name={requestSubmitter.name || requestSubmitter.email || ""}
									image={requestSubmitter.imageUrl || undefined}
									size="xs"
								/>
							</Tooltip>
						)}
					</div>
				</div>
			</div>
		)
	},
)
RequestListItem.displayName = "RequestListItem"
